/**
 * Retries a promise-returning function with a fallback option.
 * @param {Function} promiseFactory - A function that returns a promise to be retried.
 * @param {Object} options - Options for retry behavior.
 * @param {number} options.maxRetries - Maximum number of retries (default: 3).
 * @param {number} options.delayMs - Delay between retries in milliseconds (default: 1000).
 * @param {Function|null} options.fallback - A function to call if all retries fail (default: null).
 * @param {Function|null} options.onRetry - A callback function to call on each retry attempt (default: null).
 * @returns {Promise} - A promise that resolves with the result of the successful promiseFactory call or the fallback value.
 */
export async function retryWithFallback(promiseFactory, options = {}) {
  const {
    maxRetries = 3,
    delayMs = 1000,
    fallback = null,
    onRetry = null,
  } = options;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const result = await promiseFactory();
      return result;
    } catch (error) {
      const isLastAttempt = attempt === maxRetries;

      if (isLastAttempt) {
        // Execute fallback if provided, otherwise rethrow the error
        if (fallback) {
          return await fallback(error);
        }
        throw error;
      }

      // Call retry callback if provided
      if (onRetry) {
        onRetry(error, attempt + 1);
      }

      // Wait before retrying
      await new Promise((resolve) => setTimeout(resolve, delayMs));
    }
  }
}

/**
 * Retries 24 times with a 5 seconds delay (2 minutes), and refreshes the page on failure.
 * @param {Function} promiseFactory - A function that returns a promise to be retried.
 * @param {Object} options - Options for retry behavior.
 * @returns {Promise} - A promise that resolves with the result of the successful promiseFactory call or reloads the page on failure.
 */
export async function retryAndRefresh(promiseFactory, options = {}) {
  const { maxRetries = 24, delayMs = 5000 } = options;

  return retryWithFallback(promiseFactory, {
    maxRetries,
    delayMs,
    fallback: () => window.location.reload(),
  });
}
